/**
 * @file Вид каталога.
 * Отвечает за отрисовку DOM:
 * - видимость и порядок элементов в списке;
 * - счётчик результатов;
 * - пагинацию;
 * - активную кнопку сортировки.
 */

/**
 * @class
 */
class CatalogView {
  /**
   * @param {object} params
   * @param {Element} params.root - Корневой элемент Stimulus-контроллера.
   * @param {Element|null} params.list - Контейнер списка элементов.
   * @param {Element[]} params.items - DOM-узлы всех элементов каталога.
   * @param {Element|null} params.resultCount - Элемент для отображения количества результатов.
   * @param {Element|null} params.pagination - Контейнер пагинации.
   * @param {Element|null} params.emptyState - Блок «ничего не найдено».
   * @param {Element[]} [params.hideOnEmpty] - Элементы, скрываемые при пустом результате
   *   (сортировка, свитч карты).
   */
  constructor({
    root,
    list,
    items,
    resultCount,
    pagination,
    emptyState,
    hideOnEmpty
  }) {
    this.root = root
    this.list = list
    this.nodesById = new Map(
      items.map((node) => [String(node.dataset.id), node])
    )
    this.resultCount = resultCount
    this.resultCountTemplates = this.#resultCountTemplates()
    this.resultCountPluralRules = this.#resultCountPluralRules()
    this.pagination = pagination
    this.emptyState = emptyState
    this.hideOnEmpty = hideOnEmpty || []

    this.#preparePagination()
  }

  /**
   * Обновляет DOM по заданному снимку: список элементов, счётчик, пагинацию и сортировку.
   * @param {{ orderedIds: string[], pageIds: string[], page: number, pageSize: number, totalItems: number, totalPages: number }} snapshot
   * @param {string} sort - Ключ активной сортировки.
   * @param {?function(number): string} [pageHref=null] - Адрес страницы, если номера должны быть
   *   ссылками (постраничный каталог без фильтров); иначе кнопки.
   */
  render(snapshot, sort, pageHref = null) {
    this.#renderItems(snapshot)
    this.#renderEmptyState(snapshot)
    this.#renderResultCount(snapshot)
    this.#renderPagination(snapshot, pageHref)
    this.#syncSortControls(sort)
  }

  /**
   * Есть ли карточка элемента в списке. У постраничного каталога в разметке только
   * одна страница — остальные карточки догружаются по мере надобности.
   * @param {string} id
   * @returns {boolean}
   */
  hasNode(id) {
    return this.nodesById.has(String(id))
  }

  /**
   * Добавляет догруженные карточки в список скрытыми: показывает их следующий render.
   * @param {Element[]} nodes
   */
  addNodes(nodes) {
    nodes.forEach((node) => {
      const id = String(node.dataset.id)
      if (this.nodesById.has(id)) return

      node.hidden = true
      this.nodesById.set(id, node)
      this.list?.append(node)
    })
  }

  /**
   * Проверяет, принадлежит ли кнопка блоку пагинации этого вида.
   * @param {Element} button
   * @returns {boolean}
   */
  containsPaginationButton(button) {
    return this.pagination?.contains(button) || false
  }

  #renderItems(snapshot) {
    if (!this.list) return

    const visibleIds = new Set(snapshot.pageIds.map(String))
    const orderedNodes = document.createDocumentFragment()

    this.nodesById.forEach((node) => {
      node.hidden = true
    })

    snapshot.orderedIds.forEach((id) => {
      const node = this.nodesById.get(String(id))
      if (!node) return

      node.hidden = !visibleIds.has(String(id))
      orderedNodes.append(node)
    })

    this.list.append(orderedNodes)
  }

  /**
   * Показывает блок «ничего не найдено», когда фильтры не оставили ни одного элемента,
   * и скрывает элементы, бессмысленные без результатов (сортировка, свитч карты).
   */
  #renderEmptyState(snapshot) {
    if (!this.emptyState) return

    const isEmpty = snapshot.totalItems === 0
    this.emptyState.hidden = !isEmpty
    if (this.list) this.list.hidden = isEmpty
    this.hideOnEmpty.forEach((element) => {
      element.hidden = isEmpty
    })
  }

  #renderResultCount(snapshot) {
    if (!this.resultCount) return

    const count = snapshot.totalItems
    const form = this.resultCountPluralRules?.select(count)
    const template =
      this.resultCountTemplates[form] ||
      this.resultCountTemplates.other ||
      this.resultCountTemplates.base

    // Без шаблонов сохраняем прежнее поведение, а при наличии форм обновляем
    // всю фразу: иначе после фильтрации число и склонение могли бы разойтись.
    this.resultCount.textContent = template
      ? template.replaceAll('%{count}', String(count))
      : count
  }

  #resultCountTemplates() {
    if (!this.resultCount?.dataset.countTemplates) return {}

    try {
      return JSON.parse(this.resultCount.dataset.countTemplates)
    } catch {
      return {}
    }
  }

  #resultCountPluralRules() {
    if (
      !this.resultCount ||
      typeof Intl === 'undefined' ||
      typeof Intl.PluralRules !== 'function'
    )
      return null

    try {
      return new Intl.PluralRules(
        this.resultCount.dataset.countLocale || document.documentElement.lang
      )
    } catch {
      return null
    }
  }

  #preparePagination() {
    if (!this.pagination) return

    // От серверных стрелок берём только подпись и иконку: сами элементы пересобираются
    // на каждом рендере — ссылкой или кнопкой, в зависимости от состояния каталога.
    this.paginationPrevTemplate = this.#directionTemplate('.pagination__prev')
    this.paginationNextTemplate = this.#directionTemplate('.pagination__next')

    this.pagination.querySelectorAll('[data-action]').forEach((element) => {
      element.removeAttribute('data-action')
    })
  }

  #directionTemplate(selector) {
    const control = this.pagination.querySelector(selector)
    if (!control) return null

    return {
      className: selector.slice(1),
      label: control.getAttribute('aria-label'),
      icon: control.innerHTML
    }
  }

  #renderPagination(snapshot, pageHref) {
    if (!this.pagination) return

    const list = this.pagination.querySelector('.pagination__list')
    const showMore = this.pagination.querySelector('.pagination__show-more')

    this.pagination.hidden = snapshot.totalItems <= snapshot.pageSize
    if (showMore) showMore.hidden = snapshot.pageSize >= snapshot.totalItems
    if (!list) return

    const items = []
    const prevItem = this.#paginationDirectionItem(
      this.paginationPrevTemplate,
      snapshot.page === 1,
      snapshot.page - 1,
      pageHref
    )

    if (prevItem) items.push(prevItem)

    this.#paginationPages(snapshot.page, snapshot.totalPages).forEach(
      (page) => {
        items.push(this.#paginationPageItem(page, snapshot.page, pageHref))
      }
    )

    const nextItem = this.#paginationDirectionItem(
      this.paginationNextTemplate,
      snapshot.page === snapshot.totalPages,
      snapshot.page + 1,
      pageHref
    )

    if (nextItem) items.push(nextItem)
    list.replaceChildren(...items)
  }

  #paginationDirectionItem(template, hidden, page, pageHref) {
    if (!template || hidden) return null

    const item = document.createElement('li')
    item.className = 'pagination__item'

    const control = this.#paginationControl(page, pageHref)
    control.className = template.className
    control.innerHTML = template.icon
    if (template.label) control.setAttribute('aria-label', template.label)
    if (pageHref) {
      control.rel = template.className === 'pagination__prev' ? 'prev' : 'next'
    }

    item.append(control)

    return item
  }

  /**
   * Ссылка на страницу или кнопка. Ссылку клик тоже листает на месте
   * (catalog_controller#paginationClick) — href нужен краулеру и «открыть в новой вкладке».
   */
  #paginationControl(page, pageHref) {
    const control = document.createElement(pageHref ? 'a' : 'button')

    if (pageHref) {
      control.href = pageHref(page)
    } else {
      control.type = 'button'
    }

    return control
  }

  #paginationPageItem(page, currentPage, pageHref) {
    const item = document.createElement('li')
    item.className = 'pagination__item'

    if (page === 'ellipsis') {
      const ellipsis = document.createElement('span')
      ellipsis.className = 'pagination__ellipsis'
      ellipsis.setAttribute('aria-hidden', 'true')
      ellipsis.textContent = '…'
      item.append(ellipsis)
      return item
    }

    if (page === currentPage) {
      const current = document.createElement('span')
      current.className = 'pagination__page is-current'
      current.setAttribute('aria-current', 'page')
      current.textContent = page
      item.append(current)
      return item
    }

    const control = this.#paginationControl(page, pageHref)
    control.className = 'pagination__page'
    control.dataset.page = page
    control.textContent = page
    item.append(control)

    return item
  }

  #paginationPages(currentPage, totalPages) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1)
    }

    const pages = new Set([
      1,
      totalPages,
      currentPage - 1,
      currentPage,
      currentPage + 1
    ])
    const visiblePages = [...pages]
      .filter((page) => page > 0 && page <= totalPages)
      .sort((a, b) => a - b)

    return visiblePages.flatMap((page, index) => {
      const previousPage = visiblePages[index - 1]

      return previousPage && page - previousPage > 1
        ? ['ellipsis', page]
        : [page]
    })
  }

  #syncSortControls(sort) {
    const options = this.root.querySelectorAll('[data-sort]')
    const selectedOption = [...options].find(
      (option) => option.dataset.sort === sort
    )

    options.forEach((option) => {
      option.classList.toggle('is-active', option === selectedOption)
    })

    const dropdown = selectedOption?.closest('.dropdown')
    const buttonText = dropdown?.querySelector(
      '[data-dropdown-target="btn"] span'
    )

    if (buttonText) buttonText.textContent = selectedOption.textContent.trim()
  }
}

export default CatalogView
